import { useCallback, useMemo, useState } from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { Model, TreeType } from '@elementary/common-ui/types';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { getTestExecutionTotals } from 'shared/utils/testExecutions';
import { getReportAssetType } from '@elementary/common-ui/utils/assets';
import useReport from 'shared/hooks/useReport';
import { useCSVExport } from 'shared/hooks/useCSVExport';
import useTableAssets, {
  TableAssetsData,
} from '@elementary/common-ui/hooks/useTableAssets';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useGraphqlPagedQuery from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import { distinctArray } from '@elementary/common-ui/utils/common';
import {
  CATALOG_TABLE_ASSETS,
  CATALOG_TABLE_ASSETS_FILTER_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { EXPORT_PAGE_SIZE, PAGE_SIZE } from '@elementary/common-ui/constants';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import {
  CATALOG_TABLE_FILTERS,
  CATALOG_TABLE_SORT,
} from '@elementary/common-ui/constants/urlParams';
import {
  mapUrlFiltersToGraphqlFilters,
  parseGraphqlFilterOptions,
} from '@elementary/common-ui/utils/graphqlFilters';
import {
  CatalogAssetsFilterOptionsResponse,
  CatalogTableAssetPagedResponse,
  getCatalogAssetsFilterOptionsQuery,
  getCatalogTableAssetsQuery,
} from '../api';
import { CatalogTableAssetData, CatalogTableAssetResponseItem } from '../types';
import { COLUMN_TO_FIELD_MAP, FIELD_TO_COLUMN_MAP } from '../consts';
import extractFileIds from '../utils/extractFileIds';

const CatalogCsvHeaders: {
  id: keyof CatalogTableAssetData | 'lastRunAt' | 'testResults';
  title: string;
}[] = [
  { id: 'dbName', title: 'Database' },
  { id: 'schemaName', title: 'Schema' },
  { id: 'name', title: 'Name' },
  { id: 'isCritical', title: 'Is Critical Asset' },
  { id: 'assetType', title: 'Type' },
  { id: 'description', title: 'Description' },
  { id: 'owners', title: 'Owners' },
  { id: 'tags', title: 'Tags' },
  { id: 'path', title: 'Path' },
  { id: 'lastRunAt', title: 'Last Run At' },
  { id: 'testResults', title: 'Test Results' },
];

const parseModel = (
  model: Model,
  extraData?: TableAssetsData,
  treeType?: string | null,
): CatalogTableAssetData | null => {
  if (
    [
      'workbook',
      'element',
      'insight',
      'cell',
      'chart_tile',
      'sql_chart_tile',
    ].includes(model.meta?.sub_type)
  ) {
    return null;
  }
  if (
    treeType === 'bi_assets' ? !model.meta?.platform : !!model.meta?.platform
  ) {
    return null;
  }

  return {
    id: model.unique_id,
    name: model.label || model.name,
    assetType: getReportAssetType(model),
    isCritical: extraData?.isCritical || false,
    lastDataModificationTime: extraData?.lastDataModificationTime ?? null,
    owners: extraData?.owners || [],
    tags: extraData?.tags || [],
    path: model.full_path,
    configPath: model.patch_path ?? null,
    dbName: model.database_name ?? '',
    schemaName: model.schema_name ?? '',
    tableName: model.table_name ?? '',
    materialization: extraData?.materialization || '',
    description: model.description || '',
    artifactType: model.artifact_type,
    latestTestExecutions: [],
  };
};

const HIDDEN_CHILD_TYPES = new Set([
  'workbook',
  'element',
  'insight',
  'cell',
  'chart_tile',
  'sql_chart_tile',
]);

const parseAsset = (
  asset: CatalogTableAssetResponseItem,
): CatalogTableAssetData | null => {
  if (HIDDEN_CHILD_TYPES.has(asset.displayType)) {
    return null;
  }
  return {
    ...asset,
    assetType: asset.displayType,
    artifactType: asset.artifactType,
    materialization: asset.materialization || '',
    description: asset.description || '',
    latestExecution: asset.latestExecution,
  };
};

type Props = {
  treeType?: TreeType;
  search?: string;
};

export const useCatalogTableData = ({ treeType, search }: Props) => {
  const { data: reportData } = useReport();
  const { data: account } = useAccount();
  const selectedEnvId = useSelectedEnv();
  const [searchParams] = useSearchParams();

  const { exportToCSV } = useCSVExport({
    headers: CatalogCsvHeaders,
    headerIdDelimiter: '.',
  });

  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();

  const selectedFolderAssetIds = useMemo(() => {
    const assetIds = selectedTreeFolderNodes
      ?.filter((node) => node.resourceType !== 'test')
      .map((node) => node.id);
    return assetIds ? distinctArray(assetIds) : undefined;
  }, [selectedTreeFolderNodes]);

  const selectedFolderAssetIdsHash = useMemo(
    () => (selectedFolderAssetIds ? hashArray(selectedFolderAssetIds) : null),
    [selectedFolderAssetIds],
  );

  const hasSelectedNodes = useMemo(
    () => !!(selectedTreeFolderNodes?.length || selectedTreeNode?.id),
    [selectedTreeFolderNodes, selectedTreeNode],
  );

  const { urlFiltersDebounced } = useUrlFilters({
    urlKey: CATALOG_TABLE_FILTERS,
  });

  const urlSort: { id: string; direction: 'asc' | 'desc' } | null = useMemo(
    () => JSON.parse(searchParams.get(CATALOG_TABLE_SORT) || 'null') || null,
    [searchParams],
  );

  const assetFilters = useMemo(
    () =>
      mapUrlFiltersToGraphqlFilters(urlFiltersDebounced, COLUMN_TO_FIELD_MAP),
    [urlFiltersDebounced],
  );

  const sort = useMemo(() => {
    if (!urlSort) return undefined;
    const field = COLUMN_TO_FIELD_MAP[urlSort.id];
    if (!field) return undefined;
    return {
      field,
      direction: urlSort.direction.toUpperCase() as 'ASC' | 'DESC',
    };
  }, [urlSort]);

  const {
    data: selectedAssetsData,
    isFetching: isSelectedAssetsFetching,
    isLoading: isSelectedAssetsLoading,
    isError: isAssetsError,
    fetchNextPage,
    hasNextPage,
    fetchAllPages,
  } = useGraphqlPagedQuery<
    CatalogTableAssetResponseItem,
    CatalogTableAssetPagedResponse
  >({
    queryKey: [
      CATALOG_TABLE_ASSETS,
      selectedEnvId,
      selectedFolderAssetIdsHash,
      urlFiltersDebounced,
      urlSort,
      search,
    ],
    query: getCatalogTableAssetsQuery,
    variables: {
      assetIds: selectedFolderAssetIds?.length ? selectedFolderAssetIds : null,
      search: search?.trim() ? search.trim() : null,
      assetFilters: assetFilters?.length ? assetFilters : null,
      sort,
      limit: PAGE_SIZE,
    },
    select: (data) => data.assetsPaged,
    enabled: !!account?.use_assets_api && hasSelectedNodes,
    envId: selectedEnvId,
  });

  const {
    data: filterOptionsData,
    isFetching: isFilterOptionsFetching,
    isError: isFilterOptionsError,
  } = useGraphqlQuery<CatalogAssetsFilterOptionsResponse>({
    query: getCatalogAssetsFilterOptionsQuery,
    variables: {
      filters: {
        ids: selectedFolderAssetIds?.length ? selectedFolderAssetIds : null,
      },
    },
    queryKey: [
      CATALOG_TABLE_ASSETS_FILTER_OPTIONS,
      selectedEnvId,
      selectedFolderAssetIdsHash,
    ],
    enabled: !!account?.use_assets_api && hasSelectedNodes,
    envId: selectedEnvId,
  });

  const filterOptions = useMemo(
    () =>
      parseGraphqlFilterOptions(
        filterOptionsData?.assetsFilterOptions || [],
        FIELD_TO_COLUMN_MAP,
      ),
    [filterOptionsData],
  );

  const useAssetsApi = !!account?.use_assets_api;

  const models = reportData?.models;

  const folderModels = useMemo(() => {
    if (
      selectedTreeNode?.resourceType === 'folder' &&
      selectedTreeFolderNodes?.length
    ) {
      const modelIds = selectedTreeFolderNodes
        .map((node) => models?.[node.id]?.unique_id)
        .filter((id): id is string => !!id);
      if (modelIds.length) return modelIds;
    }

    const biAssetsFilesIds = extractFileIds(reportData?.groups?.bi_assets);
    if (biAssetsFilesIds && treeType === 'bi_assets') return biAssetsFilesIds;

    if (models) return Object.keys(models);

    return [];
  }, [
    selectedTreeNode?.resourceType,
    selectedTreeFolderNodes,
    models,
    treeType,
    reportData?.groups?.bi_assets,
  ]);

  const { data: tableAssetsExtraData, isFetching: isTableAssetsFetching } =
    useTableAssets();

  const data = useMemo(() => {
    if (account?.use_assets_api) {
      if (!hasSelectedNodes) return [];
      return (
        (selectedAssetsData
          ?.map(parseAsset)
          .filter(Boolean) as CatalogTableAssetData[]) || []
      );
    }

    if (isTableAssetsFetching) return []; // wait for selected assets to load

    return (
      (folderModels
        .map((model) => {
          const uniqId = models?.[model]?.unique_id || '';
          const currentModel = models?.[model];
          if (!currentModel) return null;
          const tableAssetExtraData = tableAssetsExtraData.get(uniqId);
          return parseModel(currentModel, tableAssetExtraData, treeType);
        })
        .filter(Boolean) as CatalogTableAssetData[]) || []
    );
  }, [
    account?.use_assets_api,
    isTableAssetsFetching,
    folderModels,
    selectedAssetsData,
    hasSelectedNodes,
    models,
    tableAssetsExtraData,
    treeType,
  ]);

  const [isExporting, setIsExporting] = useState(false);

  const mapAssetsToCsvData = useCallback(
    (assets: CatalogTableAssetData[]) =>
      assets.map((r) => {
        const totals = getTestExecutionTotals(r.latestTestExecutions);
        const latestRunTime =
          r.latestTestExecutions?.[0]?.execution?.executionTimestamp;

        return {
          ...r,
          lastRunAt: latestRunTime
            ? localizeDate(latestRunTime).format('L LTS')
            : '',
          testResults: [
            totals?.passed > 0 ? `${totals?.passed} pass` : '',
            totals?.warnings > 0 ? `${totals?.warnings} warn` : '',
            totals?.errors > 0 || totals?.failures > 0
              ? `${(totals?.errors || 0) + (totals?.failures || 0)} err`
              : '',
          ]
            .filter(Boolean)
            .join(', '),
        };
      }),
    [],
  );

  const onCSVDownload = useCallback(async () => {
    setIsExporting(true);
    try {
      if (useAssetsApi) {
        const allAssets = await fetchAllPages({ limit: EXPORT_PAGE_SIZE });
        const parsedAssets = allAssets
          .map(parseAsset)
          .filter(Boolean) as CatalogTableAssetData[];
        const csvData = mapAssetsToCsvData(parsedAssets);
        exportToCSV(csvData as Record<string, any>[], 'catalog.csv');
      } else {
        const csvData = mapAssetsToCsvData(data);
        exportToCSV(csvData as Record<string, any>[], 'catalog.csv');
      }
    } finally {
      setIsExporting(false);
    }
  }, [useAssetsApi, fetchAllPages, mapAssetsToCsvData, exportToCSV, data]);

  return {
    folderModels,
    data,
    filterOptions: useAssetsApi ? filterOptions : null,
    onCSVDownload,
    isExporting,
    isAssetsFetching: useAssetsApi
      ? isSelectedAssetsFetching || isSelectedAssetsLoading
      : false,
    isAssetsError: useAssetsApi ? isAssetsError || isFilterOptionsError : false,
    isFilterOptionsFetching: useAssetsApi ? isFilterOptionsFetching : false,
    fetchNextPage: useAssetsApi ? fetchNextPage : undefined,
    hasNextPage: useAssetsApi ? hasNextPage : false,
    isTableAssetsFetching,
  };
};
